<template>
    <div>
        <input
            ref="fileInput"
            type="file"
            multiple
            class="hidden"
            :accept="accept"
            @change="onFilesPicked"
        />

        <ul v-if="modelValue.length" class="flex flex-wrap gap-2 mb-2">
            <li
                v-for="item in modelValue"
                :key="item.key"
                class="flex items-center gap-2 max-w-full rounded-md border px-2.5 py-1.5 text-xs"
                :class="
                    item.error
                        ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-500/50 dark:bg-red-500/10 dark:text-red-300'
                        : 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200'
                "
            >
                <Icon icon="paperclip" class="w-3.5 h-3.5 shrink-0" />
                <span class="truncate max-w-[12rem]" :title="item.name">{{ item.name }}</span>
                <span v-if="item.error" class="shrink-0">{{ item.error }}</span>
                <span v-else-if="!item.id" class="shrink-0 tabular-nums text-slate-500 dark:text-slate-400">
                    {{ item.progress }}%
                </span>
                <span v-else class="shrink-0 text-slate-500 dark:text-slate-400">{{ formatSize(item.size) }}</span>
                <button
                    type="button"
                    class="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    :aria-label="`Remove ${item.name}`"
                    @click="remove(item.key)"
                >
                    ✕
                </button>
            </li>
        </ul>

        <button
            type="button"
            class="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="modelValue.length >= maxFiles"
            @click="fileInput?.click()"
        >
            <Icon icon="paperclip" class="w-4 h-4" />
            Attach files
        </button>
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
            PDF, Word, or images · up to {{ maxFiles }} files, 10MB each
        </p>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import { useAwardBookingsStore } from '@/store/award-bookings';
import { formatFileSize as formatSize } from '@/utils/format-file-size';

const MAX_BYTES = 10 * 1024 * 1024;

const props = defineProps({
    bookingUuid: { type: String, required: true },
    // [{ key, name, size, progress, id, error }] — id is set once the upload lands.
    modelValue: { type: Array, required: true },
    maxFiles: { type: Number, default: 5 },
});

const emit = defineEmits(['update:modelValue']);

const accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif';
const store = useAwardBookingsStore();
const fileInput = ref(null);
let nextKey = 0;

const items = () => props.modelValue;

const patch = (key, changes) => {
    emit(
        'update:modelValue',
        items().map((item) => (item.key === key ? { ...item, ...changes } : item))
    );
};

const remove = (key) => {
    emit(
        'update:modelValue',
        items().filter((item) => item.key !== key)
    );
};

const uploadError = (error) => {
    // nginx / PHP reject oversized bodies before Laravel sees them, with a
    // bare 413 and no JSON errors.
    if (error?.response?.status === 413) {
        return 'File is too large to upload';
    }
    const errors = error?.response?.data?.errors;
    if (errors?.file?.length) {
        return errors.file[0];
    }
    return 'Upload failed';
};

const upload = async (item, file) => {
    try {
        const uploaded = await store.uploadAttachment(props.bookingUuid, file, (progress) => {
            if (items().some((existing) => existing.key === item.key)) {
                patch(item.key, { progress });
            }
        });
        // Removed while uploading: the server copy is swept as an orphan.
        if (items().some((existing) => existing.key === item.key)) {
            patch(item.key, { id: uploaded.id, size: uploaded.size, progress: 100 });
        }
    } catch (error) {
        if (items().some((existing) => existing.key === item.key)) {
            patch(item.key, { error: uploadError(error) });
        }
    }
};

const onFilesPicked = (event) => {
    const room = props.maxFiles - items().length;
    const files = Array.from(event.target.files || []).slice(0, Math.max(0, room));
    event.target.value = '';

    const added = files.map((file) => ({
        key: `upload-${nextKey++}`,
        name: file.name,
        size: file.size,
        progress: 0,
        id: null,
        error: file.size > MAX_BYTES ? 'Over 10MB' : null,
        file,
    }));

    emit('update:modelValue', [...items(), ...added]);

    added.filter((item) => !item.error).forEach((item) => upload(item, item.file));
};
</script>
